/**
 * Multi Document Input
 * ====================
 * 
 * JS events fired on the [data-multi-document-input]:
 * 
 *  - mdi-change (any change)
 *  - mdi-delete
 *  - mdi-add
 *  - mdi-view-updated
 * 
 */

import { each, filter, keys, map, max, toArray } from "lodash";

const errorModal: Function = window["iw"].modal.errorModal;
const fileToDataUri: Function = window["iw"].utils.fileToDataUri;
const getResizedImageSrcAsDataUri: Function = window["iw"].utils.getResizedImageSrcAsDataUri;
const safeParseJSON: Function = window["iw"].utils.safeParseJSON;

const allowedImageTypes: string[] = ["image/jpeg", "image/png", "image/gif"];
const allowedImageTypesString: string = "JPEG, PNG, GIF";

const imageFileTypeIsOk = (file: File): boolean => allowedImageTypes.indexOf(file.type) >= 0;

declare const jQuery: any;
const $ = jQuery;

interface SavedDocumentItem
{
    inputName: string;
    id: number; // WP attachment ID for main document file
    url: string; // WP attachment URL for main document file
    title: string;
    basename: string;
    deleted: boolean;
    savedImage?: SavedImage;
    newImage?: NewImage;
}

export interface SavedDocumentItemOutput
{
    inputName: string;
    id: number; // WP attachment ID for main document file
    title: string;
    deleted: boolean;
    savedImage?: SavedImageOutput;
    newImage?: NewImageOutput;
}

interface NewDocumentItem
{
    inputName: string;
    file: File;
    input: HTMLInputElement;
    title: string;
    newImage?: NewImage;
}

export interface NewDocumentItemOutput
{
    inputName: string;
    file: File;
    title: string;
    newImage?: NewImageOutput;
}

interface SavedImage
{
    id: number;
    url: string;
    title: string;
    deleted: boolean;
}

export interface SavedImageOutput
{
    id: number;
    deleted: boolean;
}

interface NewImage
{
    file: File;
    input: HTMLInputElement;
}

export interface NewImageOutput
{
    file: File;
}

export interface MdiCtrls
{
    getNewItems(): NewDocumentItemOutput[];
    getSavedItems(): SavedDocumentItemOutput[];
    updateView(): Promise<void>;
    deleteNewItems(): void;
    reset(): void;
    getErrorMessage(): string|undefined;
    getWrap(): any;
}

export interface MdiCtrlsByName
{
    [inputName: string]: MdiCtrls;
}

export function initMultiDocumentInputs($wrap: any = undefined): MdiCtrlsByName
{
    if (!$wrap)
    {
        $wrap = $(document.body);
    }
    const $mdiWraps = $wrap.find(`[data-multi-document-input]`);
    const mdiCtrlsByName: MdiCtrlsByName = {};
    if ($mdiWraps.length === 0)
    {
        return mdiCtrlsByName;
    }
    $mdiWraps.each(function () {
        const $mdiWrap = $(this);
        const inputName: string = $mdiWrap.attr("data-multi-document-input");
        if (!inputName)
        {
            console.warn(`[data-multi-document-input] lacks name`);
            return;
        }
        mdiCtrlsByName[inputName] = initMultiDocumentInput($mdiWrap);
    });
    return mdiCtrlsByName;
}

interface AllowedFileTypes
{
    [extension: string]: string; // file extension mapped to mime type
}

// $mdiWrap - JQuery for [data-multi-document-input]
// This func takes care of the UI contained within the [data-multi-document-input].
// Keeps the files & their inputs stored internally.
// Returns object with methods getNewItems(), getSavedItems(), etc..
export function initMultiDocumentInput($mdiWrap): MdiCtrls
{
    const inputName: string = $mdiWrap.attr("data-multi-document-input") || "";
    const maxNumDocuments: number = parseInt($mdiWrap.attr("data-max-documents"), 10) || -1;
    const maxFileSize: number = parseInt($mdiWrap.attr("data-max-file-size"), 10) || 0; // bytes
    const maxFileSizeDisplay: string = $mdiWrap.attr("data-max-file-size-display") || "";
    const allowedFileTypeMap: AllowedFileTypes = safeParseJSON($mdiWrap.attr("data-allowed-file-types") || "{}") || {};
    const allowedFileExtensions: string[] = keys(allowedFileTypeMap);
    const allowedFileTypes: string[] = toArray(allowedFileTypeMap);
    const allowedFileExtensionsString: string = allowedFileExtensions.join(", ");
    const thumbRequired: boolean = $mdiWrap.is("[data-thumb-required]");

    const maxItemsReachedMessage: string = (maxNumDocuments === 1) ?
        `Only one document is allowed` :
        `There is a maximum of ${maxNumDocuments} documents allowed`;
    
    const rowTemplateHtml: string = $.trim($mdiWrap.find("[data-row-template]").html() || "");
    const thumbWidth: number = parseInt($mdiWrap.attr("data-thumb-width"), 10) || 150;
    const thumbHeight: number = parseInt($mdiWrap.attr("data-thumb-height"), 10) || 150;
    
    const $savedRowsWrap = $mdiWrap.find("[data-saved-rows]");
    const $newRowsWrap = $mdiWrap.find("[data-new-rows]");
    
    const savedItems: SavedDocumentItem[] = safeParseJSON($mdiWrap.attr("data-default-items") || "[]") || [];
    const newItems: NewDocumentItem[] = [];

    const fileFileTypeIsOk = (file: File): boolean => {
        return allowedFileTypes.indexOf(file.type) >= 0;
    };

    const getUndeletedSavedItems = (): SavedDocumentItem[] => filter(savedItems, (item: SavedDocumentItem) => !item.deleted);
    
    const getTotalNumItems = (): number => newItems.length + getUndeletedSavedItems().length;

    const maxItemsReached = (): boolean => ((maxNumDocuments > 0) && (getTotalNumItems() >= maxNumDocuments));

    const numRemainingItems = (): number => {
        if (maxNumDocuments <= 0)
        {
            return -1;
        }
        return max([0, maxNumDocuments - getTotalNumItems()]);
    };

    const numItemsIsOk = (numItems: number): boolean => {
        const maxNum: number = numRemainingItems();
        return (maxNum < 0) || (numItems <= maxNum);
    };

    const updateWrapClass = (): void => {
        if (getTotalNumItems() === 0)
        {
            $mdiWrap.addClass("mdi-empty");
        }
        else
        {
            $mdiWrap.removeClass("mdi-empty");
        }
        if (maxItemsReached())
        {
            $mdiWrap.addClass("mdi-max-reached");
        }
        else
        {
            $mdiWrap.removeClass("mdi-max-reached");
        }
    };
    
    const renderNewItemRows = async (): Promise<void> => {
        $newRowsWrap.empty();
        const numItems: number = newItems.length;
        for (let itemIndex = 0; itemIndex < numItems; ++itemIndex)
        {
            const item: NewDocumentItem = newItems[itemIndex];
            const updateTitle = updateItemTitle.bind(null, item);
            const deleteItem = deleteNewItemAtIndex.bind(null, itemIndex);
            const updateImage = updateNewItemImageAtIndex.bind(null, itemIndex);
            const undoDeleteItem = () => {};
            $newRowsWrap.append(await itemToRow(
                item,
                $mdiWrap,
                maxFileSize,
                maxFileSizeDisplay,
                rowTemplateHtml,
                updateTitle,
                deleteItem,
                undoDeleteItem,
                updateImage,
                thumbWidth,
                thumbHeight));
        }
    };

    const renderSavedItemRows = async (): Promise<void> => {
        $savedRowsWrap.empty();
        const numItems: number = savedItems.length; // both deleted & undeleted - this is necessary
        for (let itemIndex = 0; itemIndex < numItems; ++itemIndex)
        {
            const item: SavedDocumentItem = savedItems[itemIndex];
            const updateTitle = updateItemTitle.bind(null, item);
            const deleteItem = deleteSavedItemAtIndex.bind(null, itemIndex);
            const undoDeleteItem = undoDeleteSavedItemAtIndex.bind(null, itemIndex);
            const updateImage = updateSavedItemImageAtIndex.bind(null, itemIndex);
            $savedRowsWrap.append(await itemToRow(
                item,
                $mdiWrap,
                maxFileSize,
                maxFileSizeDisplay,
                rowTemplateHtml,
                updateTitle,
                deleteItem,
                undoDeleteItem,
                updateImage,
                thumbWidth,
                thumbHeight));
        }
    };

    const updateView = async (): Promise<void> => {
        updateWrapClass();
        await Promise.all([
            renderSavedItemRows(),
            renderNewItemRows()
        ]);
        $mdiWrap.trigger("mdi-view-updated");
    };

    const updateItemTitle = (item: SavedDocumentItem|NewDocumentItem, title: string): void => {
        item.title = title || "";
        $mdiWrap.trigger("mdi-change");
    };

    const removeNewItemFileInputs = (item: NewDocumentItem) => {
        const input: HTMLInputElement = item.newImage?.input;
        if (input)
        {
            $(input).parent().remove();
        }
        $(item.input).parent().remove();
    };
    
    const deleteNewItemAtIndex = (itemIndex: number): void => {
        if (!newItems[itemIndex])
        {
            throw new Error(`deleteNewItemAtIndex passed bad index ${itemIndex}`);
        }
        removeNewItemFileInputs(newItems[itemIndex]); // keep dom tidy
        newItems.splice(itemIndex, 1);
        $mdiWrap.trigger("mdi-delete").trigger("mdi-change");
        updateView();
    };

    const deleteSavedItemAtIndex = (itemIndex: number): void => {
        if (!savedItems[itemIndex])
        {
            throw new Error(`deleteSavedItemAtIndex passed bad index ${itemIndex}`);
        }
        savedItems[itemIndex].deleted = true;
        $mdiWrap.trigger("mdi-delete").trigger("mdi-change");
        updateView();
    };

    const undoDeleteSavedItemAtIndex = (itemIndex: number): void => {
        if (!savedItems[itemIndex])
        {
            throw new Error(`undoDeleteSavedItemAtIndex passed bad index ${itemIndex}`);
        }
        if (maxItemsReached())
        {
            errorModal(maxItemsReachedMessage);
            return;
        }
        savedItems[itemIndex].deleted = false;
        $mdiWrap.trigger("mdi-undo-delete").trigger("mdi-change");
        updateView();
    };

    const undoAllSavedItemDeletes = (): void => {
        each(savedItems, (item: SavedDocumentItem) => {
            if (maxItemsReached())
            {
                errorModal(maxItemsReachedMessage);
                return false;
            }
            item.deleted = false;
        });
        $mdiWrap.trigger("mdi-change");
        updateView();
    };

    const updateSavedItemImageAtIndex = (itemIndex: number, image?: NewImage, restoreSavedImage?: boolean): void => {
        if (!savedItems[itemIndex])
        {
            throw new Error(`updateSavedItemImageAtIndex passed bad index ${itemIndex}`);
        }
        const item: SavedDocumentItem = savedItems[itemIndex];
        const oldImageInput = item.newImage?.input;
        if (oldImageInput)
        {
            $(oldImageInput).parent().remove(); // keep dom tidy
        }
        if (item.newImage || image)
        {
            // Replace or delete the new image on the saved item
            item.newImage = image || null;
        }
        else if (item.savedImage)
        {
            item.savedImage.deleted = (restoreSavedImage === undefined) ? true : !restoreSavedImage;
        }
        $mdiWrap.trigger("mdi-change");
        updateView();
    };

    const updateNewItemImageAtIndex = (itemIndex: number, image?: NewImage): void => {
        if (!newItems[itemIndex])
        {
            throw new Error(`updateNewItemImageAtIndex passed bad index ${itemIndex}`);
        }
        const item: NewDocumentItem = newItems[itemIndex];
        const oldImageInput = item.newImage?.input;
        if (oldImageInput)
        {
            $(oldImageInput).parent().remove(); // keep dom tidy
        }
        item.newImage = image || null;
        $mdiWrap.trigger("mdi-change");
        updateView();
    };

    const deleteAllNewItems = (): void => {
        if (newItems.length === 0)
        {
            return;
        }
        each(newItems, (item: NewDocumentItem, itemIndex: number) => {
            removeNewItemFileInputs(newItems[itemIndex]); // keep dom tidy
        });
        newItems.length = 0;
        updateView();
        $mdiWrap.trigger("mdi-delete").trigger("mdi-change");
    };

    const addItem = (): void => {
        if (maxItemsReached())
        {
            errorModal(maxItemsReachedMessage);
            return;
        }
        
        const $fileInput = $(`<input type="file" ${(maxNumDocuments === 1) ? "" : 'multiple="multiple" '}/>`);
        const $fileInputWrap = $(`<div style="display:none!important"></div>`).appendTo($mdiWrap).append($fileInput);
        
        $fileInput.on("change", function onFileInputChange(event) {
            const fileInput: HTMLInputElement = $fileInput[0];
            
            if (fileInput.files.length === 0)
            {
                // User aborts add document
                $fileInputWrap.remove();
                return;
            }

            if (!numItemsIsOk(fileInput.files.length))
            {
                // System aborts add document
                $fileInputWrap.remove();
                errorModal(maxItemsReachedMessage);
                return;
            }

            const itemsToAdd: NewDocumentItem[] = [];

            each(fileInput.files, (file: File) => {
                if (maxFileSize >= 0 && file.size > maxFileSize)
                {
                    // System aborts add document
                    errorModal(`File "${file.name}" is too large!` + (maxFileSizeDisplay ? ` Maximum size is ${maxFileSizeDisplay}.` : ``));
                    $fileInputWrap.remove();
                    itemsToAdd.length = 0;
                    return false;
                }

                if (!fileFileTypeIsOk(file))
                {
                    // System aborts add document
                    errorModal(`File "${file.name}" has an invalid file type. Allowed file types are: ` + allowedFileExtensionsString);
                    $fileInputWrap.remove();
                    itemsToAdd.length = 0;
                    return false;
                }
                
                itemsToAdd.push({
                    file,
                    input: fileInput,
                    inputName,
                    newImage: null,
                    title: file.name
                });
            });

            if (itemsToAdd.length > 0)
            {
                each(itemsToAdd, (item: NewDocumentItem) => { newItems.push(item); });
                $mdiWrap.trigger("mdi-add").trigger("mdi-change");
                updateView();
            }
        }).trigger("click");
    };

    $mdiWrap.find("[data-add]").on("click", function (event) {
        addItem();
    });
    
    const init = async () => {
        await updateView();
        $mdiWrap.trigger("mdi-init");
    };

    init();
    
    return {
        getNewItems(): NewDocumentItemOutput[] {
            return map(newItems, (item: NewDocumentItem): NewDocumentItemOutput => {
                return {
                    inputName,
                    file: item.file,
                    title: item.title,
                    newImage: item.newImage ? {
                        file: item.newImage.file
                    } : null,
                };
            });
        },

        getSavedItems(): SavedDocumentItemOutput[] {
            return map(savedItems, (item: SavedDocumentItem): SavedDocumentItemOutput => {
                return {
                    inputName,
                    id: item.id,
                    deleted: item.deleted,
                    title: item.title,
                    newImage: item.newImage ? {
                        file: item.newImage.file
                    } : null,
                    savedImage: item.savedImage ? {
                        id: item.savedImage.id,
                        deleted: item.savedImage.deleted
                    } : null,
                };
            });
        },

        reset(): void {
            // Note - deleteAllNewItems must execute before undoAllSavedItemDeletes:
            deleteAllNewItems();
            undoAllSavedItemDeletes();
        },

        updateView(): Promise<void> {
            return updateView();
        },

        deleteNewItems(): void {
            deleteAllNewItems();
        },

        getErrorMessage(): string|undefined {
            if (!thumbRequired) {
                return undefined;
            }
            for (let item of newItems) {
                if (!item.newImage) {
                    return `Document thumbnails are required`;
                }
            }
            return undefined;
        },

        getWrap() {
            return $mdiWrap;
        }
    };
}

function selectNewImage(
    $mdiWrap,
    maxFileSize: number,
    maxFileSizeDisplay: string
    ): Promise<NewImage|undefined>
{
    const $fileInput = $(`<input type="file" />`);
    const $fileInputWrap = $(`<div style="display:none!important"></div>`).appendTo($mdiWrap).append($fileInput);

    return new Promise<NewImage|undefined>((resolve, reject) => {
        $fileInput.on("change", function onImageFileInputChange(event) {
            const fileInput: HTMLInputElement = $fileInput[0];
            
            if (fileInput.files.length === 0)
            {
                // User aborts add image
                $fileInputWrap.remove();
                resolve(undefined);
                return;
            }

            const file: File = fileInput.files[0];

            if (maxFileSize >= 0 && file.size > maxFileSize)
            {
                // System aborts add image
                errorModal(`Image file "${file.name}" is too large!` + (maxFileSizeDisplay ? ` Maximum size is ${maxFileSizeDisplay}.` : ``));
                $fileInputWrap.remove();
                resolve(undefined);
                return;
            }

            if (!imageFileTypeIsOk(file))
            {
                // System aborts add document
                errorModal(`Image file "${file.name}" has an invalid type. Allowed image types are: ` + allowedImageTypesString);
                $fileInputWrap.remove();
                resolve(undefined);
                return;
            }

            resolve({ file, input: fileInput });
        }).trigger("click");
    });
}

async function itemToRow(
    item: NewDocumentItem|SavedDocumentItem,
    $mdiWrap,
    maxFileSize: number,
    maxFileSizeDisplay: string,
    rowTemplateHtml: string,
    updateTitle: (title: string) => void,
    deleteItem: () => void,
    undoDeleteItem: () => void,
    updateImage: (image?: NewImage, restoreSavedImage?: boolean) => void,
    thumbWidth: number,
    thumbHeight: number): Promise<any>
{
    if ((item as SavedDocumentItem).deleted)
    {
        // item is deleted SavedDocumentItem
        const $title = $(`<span class="mdi-deleted-title">`).text((item.title || "Untitled Document") + ` (${(item as SavedDocumentItem).basename || ""})`);
        const $undo = $(`<span class="mdi-undo-delete">Undo</span>`);
        const $row = $(`<div class="mdi-deleted-saved-item-row">Deleted </div>`).append($title).append($undo);
        $undo.on("click", function (event) {
            undoDeleteItem();
            return false;
        });
        return $row;
    }

    const $row = $(rowTemplateHtml);
    const image: NewImage = item.newImage;
    const savedImage: SavedImage = image ? undefined : (item as SavedDocumentItem).savedImage;

    const $title = $row.find(`[data-title]`).val(item.title || "").on("change", function (event) {
        const title: string = $.trim($title.val() || "");
        updateTitle(title);
    });

    const addImage = async () => {
        const image: NewImage|undefined = await selectNewImage($mdiWrap, maxFileSize, maxFileSizeDisplay);
        updateImage(image);
    };

    $row.find(`[data-add-image]`).on("click", function (event) {
        addImage();
        return false;
    });

    $row.find(`[data-delete-image]`).on("click", function (event) {
        updateImage(undefined);
        return false;
    });

    $row.find(`[data-undo-delete-image]`).on("click", function (event) {
        const savedImage: SavedImage = (item as SavedDocumentItem).savedImage;
        if (!savedImage || !savedImage.deleted)
        {
            return false;
        }
        savedImage.deleted = false;
        updateImage(undefined, true);
        return false;
    });

    $row.find(`[data-delete]`).on("click", function (event) {
        deleteItem();
        return false;
    });

    let $img: any = undefined;

    if (savedImage && !savedImage.deleted)
    {
        $img = $(`<img class="mdi-thumbnail" />`).attr("src", savedImage.url)
    }
    else if (image)
    {
        $img = await createThumbnailFromFile(image.file, thumbWidth, thumbHeight);
    }

    $row.find("[data-thumb]").append($img || $(`<span class="mdi-no-image">`));
    if (image || (savedImage && !savedImage.deleted))
    {
        $row.addClass("has-image");
    }

    if (!image && (savedImage && savedImage.deleted))
    {
        $row.addClass("saved-image-deleted");
    }

    if ((item as NewDocumentItem).file)
    {
        // is NewDocumentItem
        $row.addClass("mdi-new-item").find(`[data-file-name]`).text("File: " + (item as NewDocumentItem).file.name);
    }
    else
    {
        // is SavedDocumentItem
        $row.addClass("mdi-saved-item").find(`[data-file-name]`).text("File: " + (item as SavedDocumentItem).basename);
    }

    return $row;
}

// Creates a probably-unique string identifier for a File
function fileToString(file: File): string
{
    return `${file.name}%${file.size}`;
}

const thumbDataUriCache: { [fileString: string]: string } = {}; // filestring  => dataUri

// Returns JQuery
async function createThumbnailFromFile(file: File, width: number, height: number): Promise<any>
{
    const fs: string = fileToString(file);
    let src: string;
    if (thumbDataUriCache[fs])
    {
        src = thumbDataUriCache[fs];
    }
    else
    {
        try
        {
            let dataUri: string = await fileToDataUri(file);
            if (!dataUri)
            {
                throw new Error(`No dataUri created by fileToDataUri`);
            }
            if (width && height)
            {
                dataUri = await getResizedImageSrcAsDataUri(dataUri, width, height);
            }
            src = thumbDataUriCache[fs] = dataUri;
        }
        catch (error) 
        {
            return $(`<div class="mdi-failed-image">`);
        }
    }
    const $img = $(`<img class="mdi-thumbnail" />`);
    $img.attr("src", src);
    return $img;
}
